import type {
  CampaignAnalyticsGranularity,
  CreateProductLineBody,
  ProductCenterDimension,
  ProductCenterProductRow,
  ProductCenterProductsResponse,
  ProductCenterSort,
  ProductLine,
} from "@adpilot/shared";
import { IconAlertTriangle, IconBox, IconExternalLink } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { PageHeader } from "@/components/patterns/page-header";
import { ErrorState } from "@/components/shared/error-state";
import { PerformancePanel } from "@/components/shared/performance/PerformancePanel";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { CreateProductLineDialog, type CreationShopProps } from "./CreateProductLineDialog";
import { ProductTable, ProductTableSkeleton } from "./ProductTable";
import { type ProductCenterFilters, ProductToolbar } from "./ProductToolbar";
import {
  buildProductPerformance,
  PRODUCT_CENTER_DEFAULT_METRICS,
  PRODUCT_CENTER_METRICS,
  type ProductCenterMetric,
} from "./product-performance";

export { CreateProductLineDialog } from "./CreateProductLineDialog";
export { ProductLineFilter } from "./ProductLineFilter";
export { ProductTable } from "./ProductTable";
export type { ProductCenterFilters } from "./ProductToolbar";
export { MAX_PRODUCT_LINE_ASINS, parseAsinList } from "./product-line-asins";
export {
  buildProductPerformance,
  PRODUCT_CENTER_METRICS,
  type ProductCenterMetric,
} from "./product-performance";

interface AllProductsViewProps extends CreationShopProps {
  reportingFilters?: ReactNode;
  data: ProductCenterProductsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  dimension: ProductCenterDimension;
  granularity: CampaignAnalyticsGranularity;
  metric: ProductCenterMetric;
  search: string;
  filters: ProductCenterFilters;
  productLines: ProductLine[];
  isProductLinesLoading: boolean;
  isProductLinesError: boolean;
  productLineCandidates: ProductCenterProductRow[];
  isLoadingProductLineCandidates: boolean;
  isProductLineCandidatesError: boolean;
  hasMoreProductLineCandidates: boolean;
  productLineIds: string[];
  sortBy: ProductCenterSort;
  sortDirection: "asc" | "desc";
  page: number;
  pageSize: number;
  onDimensionChange: (dimension: ProductCenterDimension) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  onMetricChange: (metric: ProductCenterMetric) => void;
  onSearchChange: (search: string) => void;
  onFiltersChange: (filters: ProductCenterFilters) => void;
  onProductLineChange: (productLineIds: string[]) => void;
  onRetryProductLines: () => void;
  onProductLineCandidateSearch: (search: string) => void;
  onLoadMoreProductLineCandidates: () => void;
  onRetryProductLineCandidates: () => void;
  onProductLineDialogOpenChange: (open: boolean) => void;
  onCreateProductLine: (input: CreateProductLineBody) => Promise<ProductLine>;
  isCreatingProductLine: boolean;
  onSortChange: (sortBy: ProductCenterSort, direction: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onClearFilters: () => void;
  onRetry: () => void;
}

/**
 * Dense product-analysis workstation matching the Paper All Products states.
 * Seller values deliberately stay nullable until product-grain SP-API reports exist.
 */
export function AllProductsView({
  reportingFilters,
  creationShops,
  creationShopId,
  onCreationShopChange,
  data,
  isLoading,
  isFetching,
  isError,
  dimension,
  granularity,
  metric,
  search,
  filters,
  productLines,
  isProductLinesLoading,
  isProductLinesError,
  productLineCandidates,
  isLoadingProductLineCandidates,
  isProductLineCandidatesError,
  hasMoreProductLineCandidates,
  productLineIds,
  sortBy,
  sortDirection,
  page,
  pageSize,
  onDimensionChange,
  onGranularityChange,
  onMetricChange,
  onSearchChange,
  onFiltersChange,
  onProductLineChange,
  onRetryProductLines,
  onProductLineCandidateSearch,
  onLoadMoreProductLineCandidates,
  onRetryProductLineCandidates,
  onProductLineDialogOpenChange,
  onCreateProductLine,
  isCreatingProductLine,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onClearFilters,
  onRetry,
}: AllProductsViewProps): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const performance = useMemo(() => buildProductPerformance(data, t), [data, t]);
  const total = data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, total);
  const hasAppliedFilters =
    search.trim().length > 0 ||
    filters.active !== "true" ||
    filters.productStatus !== "all" ||
    productLineIds.length > 0;

  return (
    <main className="flex min-h-full min-w-0 shrink-0 flex-col gap-4 px-6 pb-6 pt-5">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-warning-foreground">
          {t("eyebrow")}
        </span>
        <PageHeader
          filters={reportingFilters}
          title={t("title")}
          description={t("description")}
          actions={
            <CreateProductLineDialog
              creationShops={creationShops}
              creationShopId={creationShopId}
              onCreationShopChange={onCreationShopChange}
              rows={productLineCandidates}
              isLoadingRows={isLoadingProductLineCandidates}
              isRowsError={isProductLineCandidatesError}
              hasMoreRows={hasMoreProductLineCandidates}
              onSearchRows={onProductLineCandidateSearch}
              onLoadMoreRows={onLoadMoreProductLineCandidates}
              onRetryRows={onRetryProductLineCandidates}
              onOpenChange={onProductLineDialogOpenChange}
              onCreate={onCreateProductLine}
              isCreating={isCreatingProductLine}
            />
          }
          className="h-auto min-h-[54px]"
        />
      </div>

      <section
        className="flex items-center justify-between gap-4"
        aria-label={t("dimension.label")}
      >
        <SegmentControl<ProductCenterDimension>
          value={dimension}
          onValueChange={onDimensionChange}
          aria-label={t("dimension.label")}
        >
          {(["PRODUCT_LINE", "PARENT_ASIN", "ASIN", "SKU"] as const).map((option) => (
            <SegmentControlItem key={option} value={option} aria-label={t(`dimension.${option}`)}>
              {t(`dimension.${option}`)}
            </SegmentControlItem>
          ))}
        </SegmentControl>
        {isFetching && !isLoading ? (
          <span className="text-[10px] text-muted-foreground" aria-live="polite">
            {t("loading")}
          </span>
        ) : null}
      </section>

      {data?.currencyApproximate ? (
        <Alert>
          <AlertTitle>{t("scope.converted", { currency: data.currency })}</AlertTitle>
          <AlertDescription>{t("scope.convertedDescription")}</AlertDescription>
        </Alert>
      ) : null}
      {data?.excludedShops ? (
        <Alert variant="warning">
          <AlertTitle>{t("scope.excluded", { count: data.excludedShops })}</AlertTitle>
        </Alert>
      ) : null}
      {data?.coverage.seller.status === "unavailable" ? (
        <SellerCoverageAlert reason={data.coverage.seller.reason} />
      ) : null}
      {data && data.coverage.ads.status !== "available" ? (
        <Alert variant="warning">
          <IconAlertTriangle aria-hidden />
          <AlertTitle>{t(`adsCoverage.${data.coverage.ads.status}.title`)}</AlertTitle>
          <AlertDescription>
            {t(`adsCoverage.${data.coverage.ads.status}.description`)}
          </AlertDescription>
        </Alert>
      ) : null}

      <PerformancePanel<ProductCenterMetric>
        metrics={PRODUCT_CENTER_METRICS}
        defaultSlots={PRODUCT_CENTER_DEFAULT_METRICS}
        kpis={performance.kpis}
        trend={performance.trend}
        dataThrough={
          data?.coverage.ads.status === "available" || data?.coverage.ads.status === "partial"
            ? data.coverage.ads.endDate
            : null
        }
        metric={metric}
        granularity={granularity}
        onMetricChange={onMetricChange}
        onGranularityChange={onGranularityChange}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={onRetry}
        collapseStorageKey="adpilot:product-center-performance:v1"
        slotsStorageKey="adpilot:product-center-performance-slots:v1"
        getMetricLabel={(key) => t(`performance.metrics.${key}`)}
        title={t("performance.title")}
        subtitle={t("performance.subtitle")}
        notice={
          data?.coverage.seller.status === "unavailable" ? (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <IconAlertTriangle className="size-3.5 text-warning-foreground" aria-hidden />
              <span>{t("performance.sellerUnavailable")}</span>
              <Badge variant="neutral" size="compact">
                {t("sellerCoverage.requiresSpApi")}
              </Badge>
            </div>
          ) : data?.coverage.seller.status === "partial" ? (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <IconAlertTriangle className="size-3.5 text-warning-foreground" aria-hidden />
              <span>
                {t("sellerCoverage.partial", {
                  startDate: data.coverage.seller.startDate,
                  endDate: data.coverage.seller.endDate,
                })}
              </span>
            </div>
          ) : undefined
        }
      />

      <DataTableFrame
        className="min-h-[320px]"
        toolbar={
          <ProductToolbar
            search={search}
            filters={filters}
            productLines={productLines}
            isProductLinesLoading={isProductLinesLoading}
            isProductLinesError={isProductLinesError}
            productLineIds={productLineIds}
            onSearchChange={onSearchChange}
            onFiltersChange={onFiltersChange}
            onProductLineChange={onProductLineChange}
            onRetryProductLines={onRetryProductLines}
          />
        }
        footer={
          data && !isLoading ? (
            <DataTablePagination
              density="compact"
              page={currentPage}
              pageCount={pageCount}
              pageSize={pageSize}
              summary={t("table.summary", { from, to, total })}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          ) : undefined
        }
      >
        {isLoading ? <ProductTableSkeleton /> : null}
        {!isLoading && isError && !data ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-1">
            <ErrorState message={t("error.title")} onRetry={onRetry} />
            <p className="-mt-3 text-xs text-muted-foreground">{t("error.description")}</p>
          </div>
        ) : null}
        {!isLoading && data && data.data.length === 0 ? (
          <Empty className="min-h-[300px]">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconBox aria-hidden />
              </EmptyMedia>
              <EmptyTitle>{t("empty.title")}</EmptyTitle>
              <EmptyDescription>{t("empty.description")}</EmptyDescription>
            </EmptyHeader>
            {hasAppliedFilters ? (
              <EmptyContent>
                <Button variant="outline" size="sm" onClick={onClearFilters}>
                  {t("empty.clearFilters")}
                </Button>
              </EmptyContent>
            ) : null}
          </Empty>
        ) : null}
        {!isLoading && data && data.data.length > 0 ? (
          <ProductTable
            rows={data.data}
            currency={data.currency}
            tacosEligible={data.tacosEligibility.status === "available"}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        ) : null}
      </DataTableFrame>
    </main>
  );
}

function SellerCoverageAlert({
  reason,
}: {
  reason: "not_connected" | "not_synced" | "currency_mismatch";
}) {
  const { t } = useTranslation("productCenter");
  return (
    <Alert variant="warning">
      <IconAlertTriangle aria-hidden />
      <AlertTitle>{t(`sellerCoverage.${reason}.title`)}</AlertTitle>
      <AlertDescription>{t(`sellerCoverage.${reason}.description`)}</AlertDescription>
      <AlertAction>
        <Button
          size="xs"
          variant="outline"
          render={<Link to="/settings" search={{ tab: "amazon" }} />}
        >
          {t("sellerCoverage.openConnections")}
          <IconExternalLink data-icon="inline-end" aria-hidden />
        </Button>
      </AlertAction>
    </Alert>
  );
}
